/**
 * Section Recherche de l'onglet Import et paramètres — EF-POI-001 à
 * EF-POI-003, EF-POI-009, EF-RBK-012.
 *
 * Modifier ces réglages n'est pas un passage obligatoire avant chaque
 * recherche : l'action « Rechercher les POI » utilise les paramètres actifs.
 */

import type { JSX } from 'preact';
import { CATEGORIES } from '../../core/categories';
import { LIMITS, validateSearch } from '../../core/parameters';
import { NumberField } from '../components/NumberField';
import { formatArea, formatCount, formatDistance, formatDuration } from '../format';
import { document_, parameters, prepareSearchEstimate, searchEstimate, setParameters } from '../state';

export function SearchParametersSection(): JSX.Element {
  const current = document_.value;
  const search = parameters.value.search;
  const display = parameters.value.display;
  const routeLength = current?.route.track.lengthMeters ?? null;
  const validation = validateSearch(search, routeLength);
  const errorFor = (field: string): string | undefined =>
    validation.issues.find((issue) => issue.field === field)?.message;

  const update = (patch: Partial<typeof search>): void => {
    void setParameters({ ...parameters.value, search: { ...search, ...patch } });
    prepareSearchEstimate();
  };

  const estimate = searchEstimate.value ?? prepareSearchEstimate();

  return (
    <>
      <fieldset>
        <legend>Catégories recherchées</legend>
        <p class="muted">
          La catégorie Checkpoint n’est pas une catégorie de recherche : elle s’ajoute manuellement.
        </p>
        {CATEGORIES.filter((definition) => definition.searchable).map((definition) => (
          <label class="checkbox" key={definition.key}>
            <input
              type="checkbox"
              checked={search.categories.includes(definition.key)}
              onChange={(event) => {
                const checked = (event.currentTarget as HTMLInputElement).checked;
                const next = checked
                  ? [...search.categories, definition.key]
                  : search.categories.filter((key) => key !== definition.key);
                update({ categories: next });
              }}
            />
            <span>{definition.label}</span>
          </label>
        ))}
        {errorFor('search.categories') !== undefined ? (
          <span class="field-error">{errorFor('search.categories')}</span>
        ) : null}
      </fieldset>

      <fieldset>
        <legend>Corridor et portion</legend>
        <NumberField
          label="Corridor de recherche"
          suffix="km"
          value={search.corridorMeters / 1000}
          min={LIMITS.corridorMeters.min / 1000}
          max={LIMITS.corridorMeters.max / 1000}
          step={0.1}
          error={errorFor('search.corridorMeters')}
          onChange={(value) => {
            update({ corridorMeters: Math.round(value * 10) * 100 });
          }}
        />
        <NumberField
          label="Ignorer après le départ"
          suffix="km"
          value={search.skipStartMeters / 1000}
          min={0}
          max={routeLength === null ? 10_000 : routeLength / 1000}
          step={0.1}
          error={errorFor('search.skipStartMeters')}
          onChange={(value) => {
            update({ skipStartMeters: Math.max(0, Math.round(value * 1000)) });
          }}
        />
        <NumberField
          label="Ignorer avant l’arrivée"
          suffix="km"
          value={search.skipFinishMeters / 1000}
          min={0}
          max={routeLength === null ? 10_000 : routeLength / 1000}
          step={0.1}
          error={errorFor('search.skipFinishMeters')}
          onChange={(value) => {
            update({ skipFinishMeters: Math.max(0, Math.round(value * 1000)) });
          }}
        />
      </fieldset>

      <fieldset>
        <legend>Budget de recherche</legend>
        <NumberField
          label="Durée totale maximale"
          suffix="min"
          value={Math.round(search.budgetSeconds / 60)}
          min={LIMITS.budgetSeconds.min / 60}
          max={LIMITS.budgetSeconds.max / 60}
          step={1}
          error={errorFor('search.budgetSeconds')}
          onChange={(value) => {
            update({ budgetSeconds: Math.round(value * 60) });
          }}
        />
      </fieldset>

      <fieldset>
        <legend>Affichage</legend>
        <NumberField
          label="Seuil de regroupement des POI"
          suffix="km"
          value={display.groupingThresholdMeters / 1000}
          min={LIMITS.groupingThresholdMeters.min / 1000}
          max={LIMITS.groupingThresholdMeters.max / 1000}
          step={0.1}
          onChange={(value) => {
            void setParameters({
              ...parameters.value,
              display: { ...display, groupingThresholdMeters: Math.round(value * 10) * 100 },
            });
          }}
        />
        <p class="muted">La valeur 0 désactive le regroupement.</p>
      </fieldset>

      <section class="card">
        <h3 style="margin-top:0;">Estimation préalable</h3>
        {estimate === null ? (
          <p>Chargez un parcours pour obtenir une estimation.</p>
        ) : (
          <dl class="definition-list">
            <div>
              <dt>Tronçons</dt>
              <dd>{formatCount(estimate.chunkCount)}</dd>
            </div>
            <div>
              <dt>Longueur interrogée</dt>
              <dd>{formatDistance(estimate.interrogatedLengthMeters)}</dd>
            </div>
            <div>
              <dt>Surface du corridor</dt>
              <dd>{formatArea(estimate.corridorAreaSquareMeters)}</dd>
            </div>
            <div>
              <dt>Durée estimée</dt>
              <dd>{formatDuration(estimate.estimatedSeconds)}</dd>
            </div>
            <div>
              <dt>Catégories actives</dt>
              <dd>{estimate.categories.length}</dd>
            </div>
          </dl>
        )}
        {estimate !== null && (estimate.exceedsBudget || estimate.exceedsArea) ? (
          <p class="notice">
            Un seuil d’alerte est dépassé : une confirmation explicite sera demandée avant tout envoi.
          </p>
        ) : null}
      </section>
    </>
  );
}
